import React, { useState, useEffect, useCallback } from 'react';
import { AbstractProgramAddDialog } from './dialogs/abstract-program-add-dialog';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { activateProgramOperation } from './operation/activate-program-operation';
import globalConfigStore from '../../../../../stores/global-config-store';
import { Column } from 'primereact/column';
import dateUtils from '../../../../../utils/date-format';
import {
  DataTable,
  DataTableRowClickEvent,
  DataTablePageEvent,
} from 'primereact/datatable';
import { AbstractProgram } from '../../../../../api/types/settings/studies/abstract-program';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { CreateProgram } from './componenets/create-program';
import { Major } from '../../../../../api/types/settings/studies/major';
import { deleteAbstractProgramOperation } from './operation/delete-abstract-program-operation';
import appApi from '../../../../../api/app-api';
import { AbstractProgramsFilter } from './componenets/abstract-programs-filter';
import {
  AbstractProgramsFilterType,
  defaultAbstractProgramsFilter,
} from './abstract-programs-filter.type';

const ITEMS_PER_PAGE = 10;

// Backend Joi: `abstractProgramName: Joi.string().min(2).allow(null).optional()`.
// We send null when the input is shorter than 2 chars (after trim) so the
// request stays valid without surfacing a backend warning.
const ABSTRACT_PROGRAM_NAME_MIN_LENGTH = 2;

const normalizeAbstractProgramName = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length < ABSTRACT_PROGRAM_NAME_MIN_LENGTH) return null;
  return trimmed;
};

const ProgramsSettingsTab = observer(() => {
  const navigate = useNavigate();

  const [isAddAbstractProgramActive, setIsAddAbstractProgramActive] =
    useState(false);

  const [selectedAbstractProgram, setSelectedAbstractProgram] =
    useState<AbstractProgram | null>(null);

  // Pagination state
  const [abstractPrograms, setAbstractPrograms] = useState<AbstractProgram[]>(
    [],
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(ITEMS_PER_PAGE);

  const [filters, setFilters] = useState<AbstractProgramsFilterType>(
    defaultAbstractProgramsFilter(),
  );

  const loadAbstractPrograms = useCallback(
    async (
      offset: number,
      limit: number,
      filtersToApply: AbstractProgramsFilterType,
    ) => {
      setTableLoading(true);
      try {
        const response = await appApi.getAbstractProgramsPaginated(
          offset,
          limit,
          {
            abstractProgramName: normalizeAbstractProgramName(
              filtersToApply.abstractProgramName,
            ),
          },
        );
        if (response.data.body) {
          setAbstractPrograms(response.data.body.data);
          setTotalRecords(response.data.body.total);
        }
      } catch (error) {
        console.error('Error loading abstract programs:', error);
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  // Initial load + re-load when pagination changes. Filter changes are applied
  // explicitly via the search button (matches the journal-entries pattern).
  useEffect(() => {
    loadAbstractPrograms(first, rows, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, rows, loadAbstractPrograms]);

  const onPageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const handleSearch = async () => {
    setFirst(0);
    await loadAbstractPrograms(0, rows, filters);
  };

  const handleClear = () => {
    setFilters(defaultAbstractProgramsFilter());
  };

  const refreshData = () => {
    loadAbstractPrograms(first, rows, filters);
  };

  const onAddButtonClicked = () => {
    setIsAddAbstractProgramActive(true);
  };

  const closeDialog = (shouldRefresh?: boolean) => {
    setIsAddAbstractProgramActive(false);
    if (shouldRefresh) {
      refreshData();
    }
  };

  const [loading, setLoading] = useState(false);

  const handleActivateProgram = async (
    startYear: number,
    abstractProgramId: number,
    branchId: number,
  ) => {
    //await initializeProgram(abstractProgramId);
    await activateProgramOperation(
      branchId,
      abstractProgramId,
      startYear,
      setLoading,
      navigate,
    );
  };

  const dialogStyle = {
    width: '800px',
    backgroundColor: 'white',
    padding: '8px',
    borderRadius: '8px',
  };

  const [selectedAbstractProgramId, setSelectedAbstractProgramId] = useState<
    number | null
  >(null);

  const handleApproveSelectedStartYear = (
    startYear: number,
    branchId: number,
  ) => {
    if (selectedAbstractProgramId !== null) {
      handleActivateProgram(startYear, selectedAbstractProgramId, branchId);
    }
  };

  const [deleteProgramId, setDeleteProgramId] = useState<number | null>(null);
  const handleDeleteProgram = async (abstractProgramId: number) => {
    await deleteAbstractProgramOperation(abstractProgramId, setDeleteProgramId);
    refreshData();
  };

  return (
    <div>
      <Dialog
        header="הוספת תוכנית טיפוסית"
        visible={isAddAbstractProgramActive}
        style={dialogStyle}
        onHide={() => {
          setIsAddAbstractProgramActive(false);
        }}
      >
        <AbstractProgramAddDialog closeDialog={closeDialog} />
      </Dialog>

      <Dialog
        header="יצירת תוכנית"
        visible={selectedAbstractProgramId !== null}
        style={{
          width: '500px',
          height: '100vh',
          minHeight: '100vh',
          borderRadius: '0 8px 8px 0', // Only right-side border radius (top-right, bottom-right)

          backgroundColor: 'white',
          padding: '8px',
          position: 'absolute',
          left: '0px', // Positions it from the left edge
          top: '0', // Aligns to the top of the viewport
          margin: '0', // Removes any margin
          transform: 'none', // Removes any transform that might affect positioning
          display: 'flex', // Using flex for better content organization
          flexDirection: 'column', // Stacks header and content vertically
        }}
        onHide={() => {
          setSelectedAbstractProgramId(null);
        }}
      >
        <CreateProgram
          loading={loading}
          approve={handleApproveSelectedStartYear}
          programName={
            selectedAbstractProgramId !== null
              ? abstractPrograms.find((p) => p.id === selectedAbstractProgramId)
                  ?.programName
              : undefined
          }
        />
      </Dialog>

      {selectedAbstractProgram && (
        <Dialog
          header={`תוכנית אבסרקטית ${selectedAbstractProgram.programName}`}
          visible={selectedAbstractProgram !== null}
          style={dialogStyle}
          onHide={() => setSelectedAbstractProgram(null)}
        >
          <AbstractProgramAddDialog
            abstractProgram={selectedAbstractProgram}
            closeDialog={() => setSelectedAbstractProgram(null)}
          />
        </Dialog>
      )}

      <div style={{ marginTop: '16px' }}>
        <AbstractProgramsFilter
          filters={filters}
          loading={tableLoading}
          onChange={setFilters}
          onSearch={handleSearch}
          onClear={handleClear}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '12px',
        }}
      >
        <Button
          onClick={onAddButtonClicked}
          style={{ height: '30px', flexShrink: 0 }}
          label={'להוספה'}
        />
      </div>

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={abstractPrograms}
        scrollable
        onRowClick={(event: DataTableRowClickEvent) => {
          setSelectedAbstractProgram(event.data as AbstractProgram);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
        loading={tableLoading}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
      >
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="years"
          header="מגמות"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractProgram) => (
            <div>{rowData.majors.map((item) => item.majorName).join(', ')}</div>
          )}
        />
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="programName"
          header="שם"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="isActive"
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>פעיל</span>
              <i
                className="pi pi-info-circle abstract-program-is-active-info-icon"
                data-pr-tooltip="תוכנית טיפוסית שאינה פעילה תחסום פתיחת תוכנית לימודים חדשה"
                style={{ fontSize: '0.85rem', color: '#6c757d' }}
              />
            </div>
          }
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractProgram) => (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Tag
                value={rowData.isActive ? 'פעיל' : 'לא פעיל'}
                severity={rowData.isActive ? 'success' : 'danger'}
                rounded
              />
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="supervisory"
          header="גוף מפקח"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractProgram) => (
            <div>{rowData.supervisory.map((item) => item.institutionName)}</div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="years"
          header="מספר שנים"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdByName"
          header="נוצר ע״י"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdAt"
          header="נוצר בתאריך"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: AbstractProgram) => (
            <div>{dateUtils.displayFormatDate(data.createdAt)}</div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdAt"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: AbstractProgram) => (
            <Button
              outlined={true}
              severity={'info'}
              style={{ height: '30px' }}
              disabled={!data.isActive}
              tooltip={
                data.isActive
                  ? 'לחץ לפתיחת תוכנית חדשה'
                  : 'תוכנית זו אינה פעילה'
              }
              tooltipOptions={{ showOnDisabled: true }}
              label={'פתיחת תוכנית'}
              onClick={async () => setSelectedAbstractProgramId(data.id)}
            />
          )}
        />

        <Column
          style={{ textAlign: 'right' }}
          field="נשלח ע״י"
          header=""
          body={(data: Major) => (
            <Button
              icon="pi pi-trash"
              text
              severity="danger"
              tooltip={'מחיקת תוכנית'}
              loading={deleteProgramId === data.id}
              disabled={deleteProgramId !== null}
              onClick={() => handleDeleteProgram(data.id)}
            />
          )}
        />
      </DataTable>
      <Tooltip target=".abstract-program-is-active-info-icon" position="top" />
    </div>
  );
});

export default ProgramsSettingsTab;
